import React, {useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Tab, TabPanel, Tabs} from '@vio/atlas/src/components/designSystem/Tabs'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'

import {aiReviewsMessages} from '../constants'
import {useAiPoweredInsight} from '../useAiPoweredInsight'
import {getCategoryTitle} from '../utils'
import {AiPoweredSummaryItem} from './AiPoweredSummaryItem'
import {AiPoweredSummaryOverall} from './AiPoweredSummaryOverall'

import type {Hotel} from '@findhotel/sapi'

const MainWrapper = styled.div`
  overflow: hidden;
  position: relative;
  ${linariaMq.desktopXs} {
    min-width: 684px;
  }
  ${linariaMq.desktopMd} {
    min-width: 764px;
  }
`

const StyledTabs = styled(Tabs)`
  &.--ai-powered-reviews-panel-content-tabs {
    & > .scrollable {
      padding-left: ${cssTheme.layout.spacing.s400};
      padding-right: ${cssTheme.layout.spacing.s400};
      width: 100%;
    }
  }
`

const Wrapper = styled.div`
  overflow: auto;
  padding: ${cssTheme.layout.spacing.s500};
  padding-top: ${cssTheme.layout.spacing.s600};
  max-height: calc(100vh - 109px);
  overflow-y: auto;
  overscroll-behavior: contain;

  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s700};
    height: 640px;
    max-height: 100%;
  }
`

const StyledTabPanel = styled(TabPanel)`
  &.--keep-mounted.--is-active {
    padding: 0;
  }
`

interface Props {
  hotelId: Hotel['objectID']
  initialCategory?: string
  onShowAllReviews: () => void
}
export const AiPoweredReviewsPanelContent: React.FC<Props> = ({
  hotelId,
  initialCategory,
  onShowAllReviews
}) => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()
  const contentRef = useRef<HTMLDivElement>(null)

  const {data} = useAiPoweredInsight(hotelId)

  const [activeCategory, setActiveCategory] = useState(
    initialCategory ? initialCategory : 'overall'
  )

  const handleTabChange = (tabValue: string) => {
    setActiveCategory(tabValue)
    setTimeout(() => {
      contentRef.current?.scrollTo({top: 0, behavior: 'smooth'})
    }, 200)
  }

  return (
    <MainWrapper>
      <StyledTabs
        activeTab={activeCategory}
        onChange={handleTabChange}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        className="--ai-powered-reviews-panel-content-tabs"
      >
        <Tab value="overall" label={formatMessage(aiReviewsMessages.overall)} />
        {data?.categorySummaries.map(category => (
          <Tab
            key={category.category}
            value={category.category}
            label={formatMessage(getCategoryTitle(category.category))}
          />
        ))}
      </StyledTabs>
      <Wrapper ref={contentRef} data-id="AiPoweredReviewsPanelContent">
        <StyledTabPanel
          keepMounted
          value={'overall'}
          activeTab={activeCategory}
        >
          <AiPoweredSummaryOverall
            details={data}
            onSelectCategory={handleTabChange}
            onShowAllReviews={onShowAllReviews}
          />
        </StyledTabPanel>
        {data?.categorySummaries.map(category => {
          return (
            <StyledTabPanel
              keepMounted
              key={category.category}
              value={category.category}
              activeTab={activeCategory}
            >
              <AiPoweredSummaryItem
                categoryDetails={category}
                onShowAllReviews={onShowAllReviews}
              />
            </StyledTabPanel>
          )
        })}
      </Wrapper>
    </MainWrapper>
  )
}
